import { useState, useEffect } from 'react';
import prescriptionAPI from '../../../api/prescription';
import { masterDataServices } from '../../../api/masterData';
import { getImagingTests } from '../../../api/imaging';

export const usePrescription = () => {
  const [prescriptionData, setPrescriptionData] = useState({
    language: 'E',
    adultChild: 'adult',
    appointmentType: 'New',
    indoorOutdoor: 'outdoor',
    knownCaseDisease: '',
    tests: [],
    imagingTests: [],
    items: [{
      medicine: '',
      name: '',
      instruction: '',
      days: 5,
      dose: '',
      frequency: '',
      timing: {
        morning: false,
        noon: false,
        evening: false,
        night: false
      },
      route: '',
      modified: false
    }],
    foodAdvice: '',
    sloganRef: '',
    flags: {
      changeDays: false,
      addToDiary: false,
      duplicatePrint: false,
      printFormat: 'A4'
    }
  });

  const [previousPrescriptions, setPreviousPrescriptions] = useState([]);
  const [loadingPrescriptions, setLoadingPrescriptions] = useState(false);
  const [loading, setLoading] = useState(false);

  // Master data state
  const [diseases, setDiseases] = useState([]);
  const [slogans, setSlogans] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [tests, setTests] = useState([]);
  const [imagingTests, setImagingTests] = useState([]);
  const [loadingMasterData, setLoadingMasterData] = useState(false);

  // Fetch previous prescriptions for the selected patient
  const fetchPreviousPrescriptions = async (patientId) => {
    try {
      setLoadingPrescriptions(true);
      const response = await prescriptionAPI.getPatientPrescriptions(patientId, {
        limit: 10,
        sort: '-createdAt'
      });
      console.log(response);

      if (response.success) {
        setPreviousPrescriptions(response.data.prescriptions.items || []);
      }
      console.log(previousPrescriptions);
    } catch (error) {
      console.error('Error fetching previous prescriptions:', error);
      setPreviousPrescriptions([]);
    } finally {
      setLoadingPrescriptions(false);
    }
  };

  // Prescription management functions
  // Fetch master data
  const fetchMasterData = async () => {
    try {
      setLoadingMasterData(true);
      const [diseasesResponse, slogansResponse, medicinesResponse, testsResponse, imagingResponse] = await Promise.all([
        masterDataServices.diseases.getActive(),
        masterDataServices.slogans.getActive(),
        masterDataServices.medicines.getActive(),
        masterDataServices.tests.getActive(),
        getImagingTests({ limit: 1000, activeStatus: 'Active' })
      ]);

      console.log('[usePrescription] Raw responses:', {
        diseasesResponse,
        diseasesType: typeof diseasesResponse,
        diseasesKeys: diseasesResponse ? Object.keys(diseasesResponse) : 'null',
        slogansResponse,
        medicinesResponse,
        testsResponse,
        imagingResponse
      });

      // Handle both response structures: { records: [] } or { data: { records: [] } }
      const diseasesData = diseasesResponse?.records || diseasesResponse?.data?.records || [];
      const slogansData = slogansResponse?.records || slogansResponse?.data?.records || [];
      const medicinesData = medicinesResponse?.records || medicinesResponse?.data?.records || [];
      const testsData = testsResponse?.records || testsResponse?.data?.records || [];
      const imagingData = imagingResponse?.data?.data?.tests || imagingResponse?.tests || [];

      console.log('[usePrescription] Extracted data:', {
        diseasesCount: diseasesData.length,
        slogansCount: slogansData.length,
        medicinesCount: medicinesData.length,
        testsCount: testsData.length,
        imagingCount: imagingData.length,
        diseasesDataSample: diseasesData[0],
        medicinesDataSample: medicinesData[0],
        testsDataSample: testsData[0]
      });

      setDiseases(diseasesData);
      setSlogans(slogansData);
      setMedicines(medicinesData);
      setTests(testsData);
      setImagingTests(imagingData);
    } catch (error) {
      console.error('Error fetching master data:', error);
      setDiseases([]);
      setSlogans([]);
      setMedicines([]);
      setTests([]);
      setImagingTests([]);
    } finally {
      setLoadingMasterData(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  const addMedicationItem = () => {
    setPrescriptionData(prev => ({
      ...prev,
      items: [...prev.items, {
        medicine: '',
        name: '',
        instruction: '',
        days: 5,
        dose: '',
        frequency: '',
        timing: {
          morning: false,
          noon: false,
          evening: false,
          night: false
        },
        route: '',
        modified: false
      }]
    }));
  };

  const removeMedicationItem = (index) => {
    setPrescriptionData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateMedicationItem = (index, field, value) => {
    setPrescriptionData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSavePrescription = async (selectedPatient, userId) => {
    if (!selectedPatient) {
      alert('Please select a patient first');
      return false;
    }

    try {
      setLoading(true);

      // Only include medications that have an actual selection; allow zero-med prescriptions
      const validItems = prescriptionData.items.filter(item => item.medicine && item.medicine.trim());

      const prescriptionPayload = {
        patient: selectedPatient.patient._id || selectedPatient.patient,
        appointment: selectedPatient._id,
        doctor: userId,
        language: prescriptionData.language,
        appointmentType: prescriptionData.appointmentType || 'New',
        knownCaseDisease: prescriptionData.knownCaseDisease || undefined,
        tests: Array.isArray(prescriptionData.tests) ? prescriptionData.tests.filter(Boolean) : [],
        imagingTests: Array.isArray(prescriptionData.imagingTests)
          ? prescriptionData.imagingTests.filter(item => item && item.test && item.test.trim())
          : [],
        items: validItems.map(item => {
          const normalizedTiming = item.timing ? {
            morning: Boolean(item.timing.morning),
            noon: Boolean(item.timing.noon),
            evening: Boolean(item.timing.evening),
            night: Boolean(item.timing.night)
          } : undefined;
          const hasTiming = normalizedTiming
            ? Object.values(normalizedTiming).some(Boolean)
            : false;

          return {
            medicine: item.medicine,
            name: item.name,
            instruction: item.instruction,
            days: parseInt(item.days) || 5,
            dose: item.dose || '',
            frequency: item.frequency || '',
            route: item.route || '',
            timing: hasTiming ? normalizedTiming : undefined,
            modified: item.modified || false
          };
        }),
        foodAdvice: prescriptionData.foodAdvice,
        sloganRef: prescriptionData.sloganRef || undefined
      };

      console.log('Creating prescription with payload:', prescriptionPayload);

      const response = await prescriptionAPI.createPrescription(prescriptionPayload);

      if (response.success) {
        alert('Prescription saved successfully!');
        // Refresh the previous prescriptions list
        fetchPreviousPrescriptions(selectedPatient.patient._id || selectedPatient.patient);
        // Don't reset form - let user download/print after saving
        // User can manually clear using Clear button if needed
        return true;
      } else {
        alert('Failed to save prescription');
        return false;
      }
    } catch (error) {
      console.error('Error saving prescription:', error);
      alert('Error saving prescription: ' + (error.message || 'Unknown error'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPrescription = async (selectedPatient) => {
    try {
      if (!selectedPatient) {
        alert('Please select a patient first');
        return;
      }

      // Import PDF generator
      const { generatePrescriptionPDF, generatePrintHTML } = await import('../utils/pdfGenerator');

      // Get the selected slogan
      const selectedSlogan = slogans.find(slogan => slogan._id === prescriptionData.sloganRef);

      // Create patient data object
      const patientData = {
        firstName: selectedPatient.patient?.firstName || selectedPatient.patient?.name || 'N/A',
        sex: selectedPatient.patient?.sex || 'N/A',
        mobile: selectedPatient.patient?.mobile || 'N/A'
      };

      // Check if this is a duplicate print
      const isDuplicate = prescriptionData.flags.duplicatePrint;
      console.log('Duplicate flag status:', isDuplicate);
      console.log('Print format:', prescriptionData.flags.printFormat);

      if (prescriptionData.flags.printFormat === 'Receipt') {
        // For receipt format, open in new window for printing
        const printHTML = generatePrintHTML(prescriptionData, patientData, selectedSlogan, isDuplicate, { testsRecords: tests });
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printHTML);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      } else {
        // For A4 format, generate PDF
        const result = await generatePrescriptionPDF(prescriptionData, patientData, selectedSlogan, isDuplicate, { testsRecords: tests });
        if (result.success) {
          console.log('PDF generated successfully:', result.fileName);
        } else {
          alert('Error generating PDF: ' + result.error);
        }
      }
    } catch (error) {
      console.error('Error printing prescription:', error);
      alert('Error printing prescription: ' + error.message);
    }
  };

  return {
    prescriptionData,
    setPrescriptionData,
    previousPrescriptions,
    loadingPrescriptions,
    loading,
    diseases,
    slogans,
    medicines,
    tests,
    imagingTests,
    loadingMasterData,
    fetchPreviousPrescriptions,
    addMedicationItem,
    removeMedicationItem,
    updateMedicationItem,
    handleSavePrescription,
    handlePrintPrescription
  };
};
